import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { SubLineItem } from "~/features/cart/cart/models/CartData";
import type { CartEntryDetailsViewModel, ProductTableMode } from "~/features/cart/cartV2/models/ProductTableViewModel";
import { buildProductLineViewModel } from "~/features/cart/cartV2/utils/buildProductLineViewModel";
import {
  selectDescriptorForLineItem,
  type ResolvedGroupedCartEntryV2,
} from "~/features/cart/cartV2/utils/selectResolvedGroupedEntryV2";

export interface BuildCartEntryLinesInput {
  entry: ResolvedGroupedCartEntryV2;
  mode: ProductTableMode;
  initiallyExpanded?: boolean;
  subLineItemsByParentId?: Record<number, SubLineItem[]>;
  allowPriceReferenceEditing?: boolean;
  optionalPartDescriptors?: Record<number, ProductDescriptor>;
}

export function buildCopySourcePackEntryId(entry: Pick<ResolvedGroupedCartEntryV2, "packGroups">): number | null {
  const sourceGroup = entry.packGroups.find((group) => group.packEntryIds.length > 0);

  if (!sourceGroup) {
    return null;
  }

  return sourceGroup.packEntryIds[sourceGroup.packEntryIds.length - 1] ?? null;
}

export function buildCartEntryLines({
  entry,
  mode,
  initiallyExpanded = true,
  subLineItemsByParentId = {},
  allowPriceReferenceEditing = false,
  optionalPartDescriptors,
}: BuildCartEntryLinesInput): CartEntryDetailsViewModel {
  const copySourcePackEntryId = buildCopySourcePackEntryId(entry);

  return {
    initiallyExpanded,
    canCopyLine: mode === "edit" && copySourcePackEntryId !== null,
    copySourcePackEntryId,
    lines: entry.lineItems.map((lineItem) => {
      const descriptorSelection = selectDescriptorForLineItem(entry, lineItem);

      return buildProductLineViewModel({
        mode,
        lineItem,
        descriptor: descriptorSelection.descriptor,
        descriptorSelection,
        subLineItems: subLineItemsByParentId[lineItem.id] ?? [],
        allowPriceReferenceEditing,
        optionalPartDescriptors,
      });
    }),
  };
}
